import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useJobs, useCategories, useLocations } from '../hooks/useApi';
import Banner from './Banner';

const Footer = () => {
  const { data: jobsData } = useJobs();
  const { data: categoriesData } = useCategories();
  const { data: locationsData } = useLocations();
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  // Function to get main cities from locations data
  const getMainCities = (locations) => {
    const mainCities = new Set();
    console.log('Raw locations data:', locations);
    Object.values(locations).forEach(location => {
      // Split by comma and get the last part (main city)
      const parts = location.split(',');
      const mainCity = parts[parts.length - 1].trim();
      console.log('Processing location:', location, 'Main city:', mainCity);
      mainCities.add(mainCity);
    });
    return Array.from(mainCities).sort();
  };

  // Get top 5 companies with most jobs
  const topCompanies = useMemo(() => {
    if (!jobsData) return [];
    const companyCounts = {};
    
    jobsData.forEach(job => {
      const company = job.company_name || 'Other';
      companyCounts[company] = (companyCounts[company] || 0) + 1;
    });

    return Object.entries(companyCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [jobsData]);

  // Get top 5 locations with most jobs
  const topLocations = useMemo(() => {
    if (!jobsData || !locationsData) {
      console.log('Missing data:', { jobsData, locationsData });
      return [];
    }
    
    const locationCounts = {};
    console.log('Processing jobs for location counts. Total jobs:', jobsData.length);
    
    // Count jobs for each main city
    jobsData.forEach(job => {
      const jobLocation = job.location_name || job.location || '';
      console.log('Job location:', jobLocation, 'for job:', job.job_id);
      if (jobLocation) {
        // Extract main city from full location name
        const parts = jobLocation.split(',');
        const mainCity = parts[parts.length - 1].trim();
        console.log('Extracted main city:', mainCity, 'from location:', jobLocation);
        locationCounts[mainCity] = (locationCounts[mainCity] || 0) + 1;
      }
    });

    console.log('Location counts:', locationCounts);

    // Convert to array and sort by count
    const result = Object.entries(locationCounts)
      .map(([name, count]) => ({
        id: name.toLowerCase().replace(/\s+/g, '-'),
        name,
        count
      }))
      .filter(location => location.count > 0) // Only show cities with jobs
      .sort((a, b) => b.count - a.count) // Sort by count descending
      .slice(0, 5); // Take top 5

    console.log('Final top locations:', result);
    return result;
  }, [jobsData, locationsData]);

  // Get top 5 categories with most jobs
  const topCategories = useMemo(() => {
    if (!jobsData || !categoriesData) return [];
    const categoryCounts = {};
    
    jobsData.forEach(job => {
      const category = job.category_name || 'Other';
      categoryCounts[category] = (categoryCounts[category] || 0) + 1;
    });

    return Object.entries(categoryCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [jobsData, categoriesData]);

  const handleSubscribe = (e) => {
    e.preventDefault();
    // Here you would typically make an API call to subscribe the user
    setIsSubscribed(true);
    setEmail('');
  };

  return (
    <>
    <Banner/>
      <footer className="footer">
        <div className="container">
          <div className="row">
            <div className="col-md-3 col-sm-4">
              <Link to="/">
                <img className="footer-logo" src="assets/img/logo.png" alt="" loading="lazy" />
              </Link>
              <p>
              Fresher Job Adda is a leading job search platform that connects job seekers with employers. 
                Our mission is to help people find meaningful employment and help companies find the best talent.
              </p>
              {/* <div className="f-social-box">
                <ul>
                  <li>
                    <a href="#">
                      <i className="fa fa-facebook facebook-cl" />
                    </a>
                  </li>
                  <li>
                    <a href="#">
                      <i className="fa fa-google google-plus-cl" />
                    </a>
                  </li>
                  <li>
                    <a href="#">
                      <i className="fa fa-twitter twitter-cl" />
                    </a>
                  </li>
                  <li>
                    <a href="#">
                      <i className="fa fa-instagram instagram-cl" />
                    </a>
                  </li>
                </ul>
              </div> */}
            </div>
            <div className="col-md-9 col-sm-8">
              <div className="row">
                <div className="col-md-3 col-6">
                  <h4>Quick Links</h4>
                  <ul>
                    <li>
                      <Link to="/">
                        <p>
                          <i className="fa fa-angle-double-right" /> Home
                        </p>
                      </Link>
                    </li>
                    <li>
                      <Link to="/browse-jobs">
                        <p>
                          <i className="fa fa-angle-double-right" /> Jobs
                          Listing
                        </p>
                      </Link>
                    </li>
                    <li>
                      <Link to="/job-alerts">
                        <p>
                          <i className="fa fa-angle-double-right" /> Job Alerts
                        </p>
                      </Link>
                    </li>
                    <li>
                      <Link to="/privacy-policy">
                        <p>
                          <i className="fa fa-angle-double-right" /> Privacy
                          Policy
                        </p>
                      </Link>
                    </li>
                    <li>
                      <Link to="/terms-conditions">
                        <p>
                          <i className="fa fa-angle-double-right" /> Term &amp;
                          Condition
                        </p>
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="col-md-3 col-6">
                  <h4>Top Categories</h4>
                  <ul>
                    {topCategories.map(category => (
                      <li key={category.name}>
                        <Link to={`/category/${category.name.toLowerCase().replace(/\s+/g, '-')}`}>
                          <p>
                            <i className="fa fa-angle-double-right" /> {category.name}
                            <span className="badge badge-success ml-2">{category.count}</span>
                          </p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="col-md-3 col-6">
                  <h4>Top Companies</h4>
                  <ul>
                    {topCompanies.map(company => (
                      <li key={company.name}>
                        <Link to={`/company/${company.name.toLowerCase().replace(/\s+/g, '-')}`}>
                          <p>
                            <i className="fa fa-angle-double-right" /> {company.name}
                            <span className="badge badge-success ml-2">{company.count}</span>
                          </p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="col-md-3 col-6">
                  <h4>Top Locations</h4>
                  <ul>
                    {topLocations.map(location => (
                      <li key={location.name}>
                        <Link to={`/location/${location.id}`}>
                          <p>
                            <i className="fa fa-angle-double-right" /> {location.name}
                            <span className="badge badge-success ml-2">{location.count}</span>
                          </p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="copyright text-center">
                <p>Copyright © {new Date().getFullYear()} All Rights Reserved.</p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;